const readPrimitiveText = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
};

const PLACEHOLDER_VALUES = new Set([
  '',
  'false',
  'n/a',
  'na',
  'none',
  'none reported',
  'no',
  'not applicable',
  'not specified',
]);

const DISPLAY_VALUE_KEYS = [
  'label',
  'name',
  'title',
  'value',
  'text',
  'description',
  'details',
  'instructions',
  'note',
  'notes',
  'reaction',
  'medication',
  'medications',
  'chronicCondition',
  'chronicConditions',
  'emergencyNote',
  'emergencyNotes',
  'condition',
  'conditionName',
  'allergy',
  'allergyName',
  'medicalCondition',
  'medicalNotes',
];

const isPlaceholderDisplayValue = (value: string): boolean =>
  PLACEHOLDER_VALUES.has(value.trim().toLowerCase());

const uniqueDisplayValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const normalized = value.trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const collectAdmissionsProfileDisplayValues = (value: unknown): string[] => {
  const primitiveText = readPrimitiveText(value);
  if (primitiveText) {
    return isPlaceholderDisplayValue(primitiveText) ? [] : [primitiveText];
  }

  if (Array.isArray(value)) {
    return uniqueDisplayValues(value.flatMap((entry) => collectAdmissionsProfileDisplayValues(entry)));
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const record = value as Record<string, unknown>;
  return uniqueDisplayValues(
    DISPLAY_VALUE_KEYS.flatMap((key) => collectAdmissionsProfileDisplayValues(record[key])),
  );
};

export const buildAdmissionsChildMedicalSummary = (
  input: {
    specialNeeds?: unknown;
    allergies?: unknown;
    medical?: unknown;
  },
  fallback = 'No medical conditions reported.',
): string => {
  const values = uniqueDisplayValues([
    ...collectAdmissionsProfileDisplayValues(input.specialNeeds),
    ...collectAdmissionsProfileDisplayValues(input.allergies),
    ...collectAdmissionsProfileDisplayValues(input.medical),
  ]);

  return values.length > 0 ? values.join(' • ') : fallback;
};
